import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Sidebar from '../../components/shared/Sidebar';
import { FiSearch, FiEye, FiToggleLeft, FiToggleRight, FiTrash2, FiPlus } from 'react-icons/fi';

export default function AdminShops() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/admin/shops', { params: { search, status: statusFilter, page, limit: 15 } })
      .then(r => { setShops(r.data.shops); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [search, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (id, currentStatus, name) => {
    if (!window.confirm(`${name} ko ${currentStatus ? 'suspend' : 'activate'} karna chahte hain?`)) return;
    try {
      const r = await axios.patch(`/api/admin/shops/${id}/toggle`);
      toast.success(r.data.message); load();
    } catch { toast.error('Error'); }
  };

  const deleteShop = async (id, name) => {
    if (!window.confirm(`"${name}" ko delete karna chahte hain? Yeh action undo nahi ho sakta.`)) return;
    try { await axios.delete(`/api/admin/shops/${id}`); toast.success('Shop delete ho gayi'); load(); }
    catch { toast.error('Error'); }
  };

  const statusBadge = (status, isActive) => {
    if (!isActive) return <span className="badge badge-danger">Suspended</span>;
    const map = { active: 'badge-success', expired: 'badge-warning', trial: 'badge-info', suspended: 'badge-danger' };
    return <span className={`badge ${map[status] || 'badge-info'}`}>{status}</span>;
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <div className="flex-between page-header">
          <div><h1>Barber Shops</h1><p>Sab registered shops — total: {total}</p></div>
          <Link to="/admin/shops/add" className="btn btn-primary"><FiPlus /> Naya Shop Add</Link>
        </div>

        {/* Filters */}
        <div className="card mb-4">
          <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
              <input className="form-control" placeholder="Shop ya owner naam search karein..."
                style={{ paddingLeft: '36px' }} value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control" style={{ width: '180px' }} value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">Sab Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="trial">Trial</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        <div className="card">
          <div className="table-wrapper">
            {loading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div> : (
              <table>
                <thead>
                  <tr><th>Shop Name</th><th>Owner</th><th>Plan</th><th>Status</th><th>Expiry</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {shops.map(shop => (
                    <tr key={shop.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{shop.shop_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{shop.email}</div>
                      </td>
                      <td>
                        <div>{shop.owner_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{shop.phone}</div>
                      </td>
                      <td>{shop.plan_name || <span style={{ color: '#999' }}>No plan</span>}</td>
                      <td>{statusBadge(shop.subscription_status, shop.is_active)}</td>
                      <td style={{ fontSize: '13px' }}>
                        {shop.subscription_end ? new Date(shop.subscription_end).toLocaleDateString('en-PK') : '—'}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Link to={`/admin/shops/${shop.id}`} className="btn btn-outline btn-sm" title="Detail"><FiEye /></Link>
                          <button className="btn btn-sm" title={shop.is_active ? 'Suspend' : 'Activate'}
                            style={{ background: shop.is_active ? '#fff3cd' : '#d4edda', border: '1px solid #dee2e6' }}
                            onClick={() => toggleStatus(shop.id, shop.is_active, shop.shop_name)}>
                            {shop.is_active ? <FiToggleRight color="var(--success)" /> : <FiToggleLeft color="var(--danger)" />}
                          </button>
                          <button className="btn btn-danger btn-sm" title="Delete" onClick={() => deleteShop(shop.id, shop.shop_name)}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {shops.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: '40px' }}>Koi shop nahi mili</td></tr>}
                </tbody>
              </table>
            )}
          </div>

          {total > 15 && (
            <div className="flex-between mt-4" style={{ paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-light)' }}>Total: {total}</span>
              <div className="flex gap-2">
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>← Pehle</button>
                <span style={{ padding: '6px 12px', fontSize: '13px' }}>Page {page}</span>
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => p+1)} disabled={page * 15 >= total}>Agla →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
